import { getJsonData } from '../helpers/api';
import config from '../constants/config';
import { messagesSelector, userContractSelector } from '../helpers/selectors';
import { addErrorMsg } from '../utils/flashMessage/creator';
import { getAppObj, isLocalEnv, safe } from '../utils/utils';
import { kcDebugFlattenBrandKeycloak, markKcSettled } from '../utils/keycloak';
import defaultBrandData from '../mocks/defaultBrand.json';

function showFetchConfigError(state) {
  const messages = messagesSelector(state);
  const message = messages['error.get.site.config'];

  if (message) addErrorMsg(message);
  else addErrorMsg('Error while loading site config');
}

function handleLocalBrand(dispatch, userContract) {
  console.log('[KC-DEBUG] actions/brand.js/handleLocalBrand: DECISION `if (isLocalEnv())` (true => defaultBrand.json is used INSTEAD of the S3 brand for this hostname)', { isLocalEnv: isLocalEnv(), hostname: window.location.hostname, brandId: defaultBrandData?.brandId, apiBaseUrl: defaultBrandData?.apiBaseUrl, keycloakBlock: defaultBrandData?.keycloak }); // eslint-disable-line no-console
  console.log('[KC-DEBUG] actions/brand.js/handleLocalBrand: local mock keycloak block FLATTENED (on prod isLocalEnv is false, so this mock is NOT used - the S3 brand file is)', { isLocalEnv: isLocalEnv(), nodeEnv: process.env.NODE_ENV, ...kcDebugFlattenBrandKeycloak(defaultBrandData?.keycloak, 'mockKc') }); // eslint-disable-line no-console
  if (isLocalEnv()) {
    dispatch({
      type: 'brand/GET_ONE_SUCCESS',
      rawBrand: defaultBrandData,
      userContract
    });

    dispatch(setConfigFetchedState(true));
    return true;
  }
}

// noinspection JSUnusedGlobalSymbols
export function getAwsBrand() {
  return (dispatch, getState) => {
    const state = getState();
    const { awsBaseUrl, awsBrandsMapFileName } = config;
    const userContract = userContractSelector(state);

    console.log('[KC-DEBUG] actions/brand.js/getAwsBrand: ENTER - timestamped entry; everything Keycloak-related is downstream of the brand file this fetches', { atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()), awsBaseUrl, awsBrandsMapFileName, mapIsDevMap: awsBrandsMapFileName === 'brandsMapDev.json', configEnv: config.env, appSettingsEnv: getAppObj().settings?.env, hostname: window.location.hostname, origin: window.location.origin, apiUrlNow: config.getApiUrl() }); // eslint-disable-line no-console
    if (handleLocalBrand(dispatch, userContract)) return;

    const handleError = () => {
      // Config never loaded => handleKeycloak never runs => kc.init() never settles.
      // Release the request interceptor's SSO wait so this dead-end cannot deadlock a
      // request that fired before config resolved (e.g. the incomplete-user F5 path).
      markKcSettled();
      showFetchConfigError(state);
    };

    console.log('[KC-DEBUG] actions/brand.js/getAwsBrand: FIRING HTTP GET brands map', { url: awsBaseUrl + '/' + awsBrandsMapFileName, hostname: window.location.hostname, configEnv: config.env }); // eslint-disable-line no-console
    getJsonData(awsBaseUrl + '/' + awsBrandsMapFileName).then((map) => {
      const { [window.location.hostname]: brandFilePathPart } = map || {};

      console.log('[KC-DEBUG] actions/brand.js/getAwsBrand: DECISION `if (brandFilePathPart)` (hostname not in map => NO brand config => configFetched stays false => /me never called)', { hostname: window.location.hostname, brandFilePathPart, found: Boolean(brandFilePathPart) }); // eslint-disable-line no-console
      console.log('[KC-DEBUG] actions/brand.js/getAwsBrand: brands map RESOLVED - which brand FILE is about to be downloaded', { hostname: window.location.hostname, brandFilePathPart, brandFileUrl: brandFilePathPart ? awsBaseUrl + '/' + brandFilePathPart : null, mapType: typeof map, mapHostnameCount: map ? Object.keys(map).length : 0, hostnameInMap: Boolean(map && window.location.hostname in map) }); // eslint-disable-line no-console
      if (brandFilePathPart) {
        getJsonData(awsBaseUrl + '/' + brandFilePathPart).then((rawBrand) => {
          const { theme } = rawBrand || {};

          console.log('[KC-DEBUG] actions/brand.js/getAwsBrand: brand file loaded - DECISION `if (theme)`', { brandId: rawBrand?.brandId, hasTheme: Boolean(theme), apiBaseUrl: rawBrand?.apiBaseUrl, keycloakBlock: rawBrand?.keycloak }); // eslint-disable-line no-console
          console.log('[KC-DEBUG] actions/brand.js/getAwsBrand: brand file KEYCLOAK BLOCK FLATTENED - THIS is the `s3` argument selectors.js/parseKeycloakConfig receives', { brandFileUrl: awsBaseUrl + '/' + brandFilePathPart, brandId: rawBrand?.brandId, apiBaseUrl: rawBrand?.apiBaseUrl, rawBrandType: typeof rawBrand, ...kcDebugFlattenBrandKeycloak(rawBrand?.keycloak, 's3Kc') }); // eslint-disable-line no-console
          if (theme) {
            dispatch({ type: 'brand/GET_ONE_SUCCESS', rawBrand, userContract });
            dispatch(setConfigFetchedState(true));
          } else {
            console.log('[KC-DEBUG] actions/brand.js/getAwsBrand: brand file has NO theme => handleError => configFetched stays false => KC never inits', { brandId: rawBrand?.brandId, rawBrandKeys: rawBrand ? Object.keys(rawBrand).join(',') : null }); // eslint-disable-line no-console
            handleError();
          }
        }, handleError);
      } else {
        console.log('[KC-DEBUG] actions/brand.js/getAwsBrand: hostname NOT in the brands map => handleError => configFetched stays false => KC never inits', { hostname: window.location.hostname, mapKeysSample: safe(() => Object.keys(map || {}).slice(0, 20).join(',')) }); // eslint-disable-line no-console
        handleError();
      }
    }, handleError);
  };
}

export function setConfigFetchedState(payload) {
  console.log('[KC-DEBUG] actions/brand.js/setConfigFetchedState: dispatching configFetched=' + payload + ' (App.handleTokens is gated on this flag)', { payload, payloadType: typeof payload, atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()) }); // eslint-disable-line no-console
  return {
    type: 'brand/SET_CONFIG_FETCHED_STATE',
    payload
  };
}

// noinspection JSUnusedGlobalSymbols
export function setAppLoaderState(payload) {
  console.log('[KC-DEBUG] actions/brand.js/setAppLoaderState: dispatching showAppLoader=' + payload + ' (false hides the spinner - if this arrives with no user, the flow gave up silently)', { payload, payloadType: typeof payload, atIso: new Date().toISOString(), perfNowMs: Math.round(performance.now()) }); // eslint-disable-line no-console
  return {
    type: 'brand/SET_APP_LOADER_STATE',
    payload
  };
}
